import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Avatar from "react-avatar";

// third-party
import { useSelector } from "react-redux";

// project-imports
import { FEATURED, PREMIUM, SECTIONS } from "../../constants";
import SharingModal from "../reusable/SharingModal";
import Image from "../../common/footer/Images/Image";
import { useInterviewContext } from "../../context/interview_context";
import ModalLogin from "../form/ModalLogin";
import ToolTip from "../../common/Tooltip/ToolTip";
import Spinner from "../spinner-component/SpinnerOne";
import FeedbackModal from "../reusable/FeedbackModal";
import {
    GoogleAnalyticsEnum,
    triggerFunction,
} from "../../utils/google-analytics";
import BreadcrumbOne from "../../common/breadcrumb/BreadcrumbOne";

function InterviewQuestionHeader({ data }) {
    // local state initialization
    const [toggler, setToggler] = useState(false);
    const [isLoggedOut, setIsLoggedOut] = useState(false);
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [isLiked, setIsLiked] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [text, setText] = useState("");
    const [isOpen, setIsOpen] = useState(false);

    // Extracting methods and variables form the context
    const {
        getUserInteractionBySeries,
        userInteractionBySeries,
        isInteractionLoading,
        upsertSeriesUserInteraction,
    } = useInterviewContext();
    const { loginData } = useSelector((item) => item?.user);
    const location = useLocation();

    // const variable
    const currentSeries = data && data?.length ? data[0]?.slug : "";

    // Get currentUser
    const member = (loginData && loginData.data) || null;
    const currentUserId = member ? member?.userRecordId || "" : "";

    // Sharing Url
    const sharingData = data?.find((i) => i);
    const sharingUrl = `${window.location.origin}${location.pathname}`;

    // Get instructor ID
    const instructorId = process.env.REACT_APP_AUTHOR_ID;
    const instructorName = process.env.REACT_APP_AUTHOR_NAME;
    const instructorThumb = `${process.env.PUBLIC_URL}/images/instructor/${process.env.REACT_APP_AUTHOR_IMAGE}`;

    useEffect(() => {
        if (!currentUserId || !currentSeries) {
            setIsLiked(false);
            setIsBookmarked(false);
            setIsSaved(false);
            return;
        }

        const interaction = userInteractionBySeries?.find((item) => ((item?.userId) === currentUserId) && ((item?.seriesId) === currentSeries));
        const userHasLiked = interaction ? interaction?.isLiked : false;
        const userHasBookmarked = interaction ? interaction?.isBookmarked : false;
        const userHasSaved = interaction ? interaction?.isSaved : false;

        if (interaction) {
            setIsLiked(userHasLiked);
            setIsBookmarked(userHasBookmarked);
            setIsSaved(userHasSaved);
        } else {
            setIsLiked(false);
            setIsBookmarked(false);
            setIsSaved(false);
        }
    }, [userInteractionBySeries, currentUserId, currentSeries]);

    const handleBookmarkClick = async () => {
        try {
            if (!member) {
                setText("Bookmark");
                openModalLogin();
                return;
            }

            const newBookmarkState = !isBookmarked;

            setIsBookmarked(newBookmarkState);

            // Upsert API
            const body = {
                userId: currentUserId || "",
                seriesId: currentSeries,
                isBookmarked: newBookmarkState,
            };

            await upsertSeriesUserInteraction(body);

            // Google Analytics
            if (newBookmarkState) {
                triggerFunction(
                    GoogleAnalyticsEnum.BUTTON,
                    GoogleAnalyticsEnum.BOOKMARK_INTERVIEW_SERIES,
                );
            }
        } catch (error) {
            console.log(
                "141 : InterviewQuestionHeader : Error in handleBookmarkClick",
                error,
            );
        }
    };

    const handleLikeClick = async () => {
        try {
            if (!member) {
                setText("Like");
                openModalLogin();
                return;
            }

            const nextLikedState = !isLiked;

            setIsLiked(nextLikedState);

            // Upsert API
            const body = {
                userId: currentUserId || "",
                seriesId: currentSeries,
                isLiked: nextLikedState,
            };

            await upsertSeriesUserInteraction(body);

            // Google Analytics
            if (nextLikedState) {
                triggerFunction(
                    GoogleAnalyticsEnum.BUTTON,
                    GoogleAnalyticsEnum.LIKE_INTERVIEW_SERIES,
                );
            }
        } catch (error) {
            console.log(
                "177 : InterviewQuestionHeader : Error in handleLikeClick",
                error,
            );
        }
    };

    const handleSaveClick = async () => {
        try {
            if (!member) {
                setText("Save");
                openModalLogin();
                return;
            }

            const newSavedState = !isSaved;

            setIsSaved(newSavedState);

            // Upsert API
            const body = {
                userId: currentUserId || "",
                seriesId: currentSeries,
                isSaved: newSavedState,
            };

            await upsertSeriesUserInteraction(body);

            // Google Analytics
            if (newSavedState) {
                triggerFunction(
                    GoogleAnalyticsEnum.BUTTON,
                    GoogleAnalyticsEnum.SAVE_INTERVIEW_SERIES,
                );
            }
        } catch (error) {
            console.log(
                "213 : InterviewQuestionHeader : Error in handleSaveClick",
                error,
            );
        }
    };

    const toggleShareModal = () => {
        try {
            setToggler(!toggler);
        } catch (error) {
            console.error(
                "224 : InterviewQuestionHeader : Error occurred in togglePreviewModal:",
                error && JSON.stringify(error),
            );
            throw error;
        }
    };

    const toggleFeedbackModal = () => {
        try {
            if (!member) {
                setText("Leave Review");
                return openModalLogin();
            }
            setIsOpen(!isOpen);
        } catch (error) {
            console.error(
                "240 : InterviewQuestionHeader : Error occurred in toggleFeedbackModal:",
                error && JSON.stringify(error),
            );
            throw error;
        }
    };

    const openModalLogin = () => setIsLoggedOut(true);
    const closeModalLogin = () => setIsLoggedOut(false);

    useEffect(() => {
        if (!currentSeries) return;
        getUserInteractionBySeries(currentSeries);
    }, [data]);

    return (
        <div className="interview-questions-detail">
            <BreadcrumbOne
                title="Interview Q&A Details" // Breadcrumb title
                rootUrl="/"
                parentUrl="Home"
                currentUrl="Interview Q&A Details"
            />
            {isInteractionLoading && <Spinner />}
            <section className="edu-course-area eduvibe-home-five-course edu-section-gap">
                <div className="container">
                    {data?.map((elm, i) => (
                        <div className="row justify-content-between" key={i}>
                            <div className="col-xl-6 col-lg-6">
                                <div className="d-flex flex-wrap gap-3 pb--20">
                                    {elm?.featured === true && (
                                        <div>
                                            <div className="interview-header-badge text-white">
                                                {FEATURED}
                                            </div>
                                        </div>
                                    )}
                                    {elm?.isPremium === true && (
                                        <div>
                                            <div className="interview-header-badge text-white">
                                                {PREMIUM}
                                            </div>
                                        </div>
                                    )}
                                    <div>
                                        <div className="interview-header-badge text-white">
                                            {elm?.difficulty.toUpperCase() || ""}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="interview-header-badge text-white">
                                            {elm?.sections?.length || ""} {SECTIONS}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="title fs-30 mb--20">{elm?.name || ""}</h3>
                                </div>

                                <p className="mb--20 fs-15">{elm?.description || ""}</p>

                                <div className="d-flex gap-3 align-items-center flex-wrap mb--20">
                                    <div className="d-flex align-items-center">
                                        <i class="ri-checkbox-circle-line"></i>
                                        <div className="interview-header-tags">
                                            {elm?.questionCount || "0"} Questions
                                        </div>
                                    </div>

                                    <div className="d-flex align-items-center">
                                        <i class="icon-time-line"></i>
                                        <div className="interview-header-tags">
                                            {elm?.experienceRange || ""}
                                        </div>
                                    </div>
                                </div>

                                <div className="row align-items-baseline">
                                    <div className="col-12 col-sm-6">
                                        <Link
                                            to={
                                                process.env.PUBLIC_URL +
                                                `/instructor-details/${instructorId}`
                                            }
                                            className="d-flex align-items-center mb--20"
                                        >
                                            <Avatar
                                                className="custom-avatar"
                                                name={instructorName}
                                                size={34}
                                                round={true}
                                                src={instructorThumb ? instructorThumb : ""}
                                            />
                                            <span className="fs-14">{instructorName || ""}</span>
                                        </Link>
                                    </div>
                                    <div className="col-6 d-none d-sm-flex justify-content-end">
                                        <ul className="social-share bg-transparent medium-size">
                                            <li>
                                                <ToolTip title={"Like"} id={"like"} placement="bottom">
                                                    <Link
                                                        to="#"
                                                        onClick={handleLikeClick}
                                                        aria-disabled={isInteractionLoading}
                                                        style={{
                                                            pointerEvents: isInteractionLoading
                                                                ? "none"
                                                                : "auto",
                                                            color: isInteractionLoading ? "gray" : "",
                                                            cursor: isInteractionLoading
                                                                ? "not-allowed"
                                                                : "pointer",
                                                        }}
                                                    >
                                                        <i
                                                            className={
                                                                isLiked ? "ri-heart-fill" : "ri-heart-line"
                                                            }
                                                        ></i>
                                                    </Link>
                                                </ToolTip>
                                            </li>
                                            <li>
                                                <ToolTip
                                                    title={"Bookmark"}
                                                    id={"bookmark"}
                                                    placement="bottom"
                                                >
                                                    <Link
                                                        to="#"
                                                        onClick={handleBookmarkClick}
                                                        aria-disabled={isInteractionLoading}
                                                        style={{
                                                            pointerEvents: isInteractionLoading
                                                                ? "none"
                                                                : "auto",
                                                            color: isInteractionLoading ? "gray" : "",
                                                            cursor: isInteractionLoading
                                                                ? "not-allowed"
                                                                : "pointer",
                                                        }}
                                                    >
                                                        <i
                                                            className={
                                                                isBookmarked
                                                                    ? "ri-bookmark-fill"
                                                                    : "ri-bookmark-line"
                                                            }
                                                        ></i>
                                                    </Link>
                                                </ToolTip>
                                            </li>
                                            <li>
                                                <ToolTip title={"Save"} id={"save"} placement="bottom">
                                                    <Link
                                                        to="#"
                                                        onClick={handleSaveClick}
                                                        aria-disabled={isInteractionLoading}
                                                        style={{
                                                            pointerEvents: isInteractionLoading
                                                                ? "none"
                                                                : "auto",
                                                            color: isInteractionLoading ? "gray" : "",
                                                            cursor: isInteractionLoading
                                                                ? "not-allowed"
                                                                : "pointer",
                                                        }}
                                                    >
                                                        <i
                                                            className={
                                                                isSaved ? "ri-star-fill" : "ri-star-line"
                                                            }
                                                        ></i>
                                                    </Link>
                                                </ToolTip>
                                            </li>
                                            {/* <li>
                                                <ToolTip
                                                    title={"Share"}
                                                    id={"share"}
                                                    placement="bottom"
                                                >
                                                    <Link to="#" onClick={toggleShareModal}>
                                                        <i class="ri-share-fill"></i>
                                                    </Link>
                                                </ToolTip>
                                            </li> */}
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div className="col-xl-5 col-lg-6">
                                <div className="relative">
                                    <Image
                                        src={elm?.thumbnail}
                                        alt="Series Thumbnail"
                                        fill
                                        className="w-100"
                                    />
                                </div>
                                <div class="row text-center gap-3 gap-sm-0 pt--30">
                                    <div class="col-12 col-sm-6">
                                        <button
                                            className="edu-btn btn-primary btn-medium w-100"
                                            onClick={toggleFeedbackModal}
                                        >
                                            Leave Review
                                        </button>
                                    </div>
                                    <div class="col-12 col-sm-6">
                                        <button
                                            className="edu-btn btn-border btn-medium w-100"
                                            onClick={toggleShareModal}
                                        >
                                            Share
                                        </button>
                                    </div>
                                </div>

                                <div class="d-flex d-sm-none justify-content-center mt--20">
                                    <ul className="social-share bg-transparent medium-size">
                                        <li>
                                            <ToolTip title={"Like"} id={"like"} placement="bottom">
                                                <Link to="#" onClick={handleLikeClick}>
                                                    <i
                                                        className={
                                                            isLiked ? "ri-heart-fill" : "ri-heart-line"
                                                        }
                                                    ></i>
                                                </Link>
                                            </ToolTip>
                                        </li>
                                        <li>
                                            <ToolTip
                                                title={"Bookmark"}
                                                id={"bookmark"}
                                                placement="bottom"
                                            >
                                                <Link to="#" onClick={handleBookmarkClick}>
                                                    <i
                                                        className={
                                                            isBookmarked
                                                                ? "ri-bookmark-fill"
                                                                : "ri-bookmark-line"
                                                        }
                                                    ></i>
                                                </Link>
                                            </ToolTip>
                                        </li>
                                        <li>
                                            <ToolTip title={"Save"} id={"save"} placement="bottom">
                                                <Link to="#" onClick={handleSaveClick}>
                                                    <i
                                                        className={
                                                            isSaved ? "ri-star-fill" : "ri-star-line"
                                                        }
                                                    ></i>
                                                </Link>
                                            </ToolTip>
                                        </li>
                                        {/* <li>
                                            <ToolTip title={"Share"} id={"share"} placement="bottom">
                                                <Link to="#" onClick={toggleShareModal}>
                                                    <i class="ri-share-fill"></i>
                                                </Link>
                                            </ToolTip>
                                        </li> */}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
            <SharingModal
                show={toggler}
                onCloseModal={toggleShareModal}
                title={sharingData?.name}
                url={sharingUrl}
            />
            <FeedbackModal
                show={isOpen}
                setShow={setIsOpen}
                onCloseModal={toggleFeedbackModal}
                seriesId={currentSeries}
                currentUserId={currentUserId}
            />
            {/* Login modal */}
            <ModalLogin
                show={isLoggedOut}
                onCloseModalLogin={closeModalLogin}
                title={`Login to ${text}`}
            />
        </div>
    );
}

export default InterviewQuestionHeader;
